/**
 * Recent Files & Workspaces Store
 *
 * Tracks recently opened files and workspace folders in localStorage.
 * Used by both the native menu "打开最近…" and the WelcomeScreen.
 */

import { create } from "zustand";

const STORAGE_KEY = "magic-md:recent-items";
const MAX_RECENT_FILES = 20;
const MAX_RECENT_WORKSPACES = 10;

export type RecentItemType = "file" | "folder" | "workspace";

export interface RecentItem {
    /** Full path */
    path: string;
    /** Display name (file/folder name) */
    name: string;
    /** Type of item */
    type: RecentItemType;
    /** Last opened timestamp */
    openedAt: number;
}

/* ── localStorage persistence ── */

function loadRecentItems(): RecentItem[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed: unknown = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed as RecentItem[];
        }
    } catch {
        /* ignore */
    }
    return [];
}

function saveRecentItems(items: RecentItem[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
        /* ignore */
    }
}

function addItem(items: RecentItem[], item: RecentItem): RecentItem[] {
    const filtered = items.filter((i) => i.path !== item.path);
    const merged = [item, ...filtered];

    // Apply per-type limits
    const files = merged.filter((i) => i.type === "file");
    const folders = merged.filter((i) => i.type === "folder");
    const workspaces = merged.filter((i) => i.type === "workspace");

    return [
        ...files.slice(0, MAX_RECENT_FILES),
        ...folders.slice(0, MAX_RECENT_WORKSPACES),
        ...workspaces.slice(0, MAX_RECENT_WORKSPACES),
    ].sort((a, b) => b.openedAt - a.openedAt);
}

/* ── Zustand store ── */

interface RecentState {
    items: RecentItem[];

    /** Add a recently opened file */
    addRecentFile: (path: string) => void;
    /** Add a recently opened folder */
    addRecentFolder: (path: string) => void;
    /** Add a recently opened workspace (.mdspace) */
    addRecentWorkspace: (path: string) => void;
    /** Remove a specific item */
    removeItem: (path: string) => void;
    /** Clear all recent items */
    clearAll: () => void;

    /** Get recent files only */
    getRecentFiles: () => RecentItem[];
    /** Get recent folders only */
    getRecentFolders: () => RecentItem[];
    /** Get recent workspaces only */
    getRecentWorkspaces: () => RecentItem[];
}

function nameFromPath(path: string): string {
    return path.split("/").pop() ?? path;
}

export const useRecentStore = create<RecentState>((set, get) => ({
    items: loadRecentItems(),

    addRecentFile: (path: string) => {
        const isWorkspace = path.toLowerCase().endsWith(".mdspace");
        const item: RecentItem = {
            path,
            name: nameFromPath(path),
            type: isWorkspace ? "workspace" : "file",
            openedAt: Date.now(),
        };
        const items = addItem(get().items, item);
        saveRecentItems(items);
        set({ items });
    },

    addRecentFolder: (path: string) => {
        const item: RecentItem = {
            path,
            name: nameFromPath(path),
            type: "folder",
            openedAt: Date.now(),
        };
        const items = addItem(get().items, item);
        saveRecentItems(items);
        set({ items });
    },

    addRecentWorkspace: (path: string) => {
        const item: RecentItem = {
            path,
            name: nameFromPath(path),
            type: "workspace",
            openedAt: Date.now(),
        };
        const items = addItem(get().items, item);
        saveRecentItems(items);
        set({ items });
    },

    removeItem: (path: string) => {
        const items = get().items.filter((i) => i.path !== path);
        saveRecentItems(items);
        set({ items });
    },

    clearAll: () => {
        saveRecentItems([]);
        set({ items: [] });
    },

    getRecentFiles: () => get().items.filter((i) => i.type === "file"),
    getRecentFolders: () => get().items.filter((i) => i.type === "folder"),
    getRecentWorkspaces: () => get().items.filter((i) => i.type === "workspace"),
}));
